import { MapPin, Phone, Mail, MessageCircle, Clock, Search, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import logoImg from "@/assets/logo.webp";
import ScrollReveal from "./ScrollReveal";
import FooterNeuralBar from "./FooterNeuralBar";

const Footer = () => {
  return (
    <footer id="contato" className="bg-footer text-footer-fg relative overflow-hidden">
      {/* Decorative accent */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />

      <div className="container mx-auto px-4 py-14">
        <ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Left: Brand */}
            <div>
              <div className="mb-6">
                <img src={logoImg} alt="Equilybra Clínica de Psicologia" width="240" height="230" loading="lazy" decoding="async" className="h-20 w-auto object-contain header-glow-logo" />
              </div>
              <p className="text-footer-fg/75 text-sm leading-relaxed max-w-sm">
                Promovendo saúde, qualidade de vida e bem estar através de uma equipe capacitada em
                promoção de saúde mental.
              </p>
            </div>

            {/* Right: Contact info */}
            <div>
              <h3 className="font-heading font-semibold text-lg mb-5">Contato</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-primary/20 flex items-center justify-center shrink-0 mt-0.5">
                    <MapPin className="w-4 h-4 text-primary" />
                  </div>
                  <p className="text-sm text-footer-fg/75">
                    Rua Agostinho Gomes, 3319 – Ipiranga, São Paulo – SP
                    <br />
                    <span className="text-xs">Próximo ao metrô Sacomã</span>
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-primary/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Phone className="w-4 h-4 text-primary" />
                  </div>
                  <div className="text-sm text-footer-fg/75 space-y-1">
                    <a href="tel:1123050747" className="block hover:text-primary transition-colors">(11) 2305-0747</a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
                    <MessageCircle className="w-4 h-4 text-primary" />
                  </div>
                  <a
                    href="https://wa.me/5511976974905?text=Oi%21%20Vim%20pelo%20site%20da%20Equilybra%20%28Site%29%20e%20gostaria%20de%20saber%20como%20funciona."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-footer-fg/75 hover:text-primary transition-colors"
                  >
                    (11) 97697-4905 (somente mensagem)
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
                    <Mail className="w-4 h-4 text-primary" />
                  </div>
                  <a
                    href="mailto:contato@equilybra.com.br"
                    className="text-sm text-footer-fg/75 hover:text-primary transition-colors"
                  >
                    contato@equilybra.com.br
                  </a>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-primary/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Clock className="w-4 h-4 text-primary" />
                  </div>
                  <div className="text-sm text-footer-fg/75">
                    <p>Segunda a Sexta: 8h às 21h</p>
                    <p>Sábado: 8h às 16h</p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <h4 className="text-sm font-semibold mb-3">Siga-nos</h4>
                <div className="flex gap-4">
                  <a
                    href="https://www.youtube.com/c/equilybrapsicologia"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-footer-fg/50 hover:text-primary transition-colors text-sm"
                  >
                    YouTube
                  </a>
                  <a
                    href="https://www.instagram.com/equilybrainsta/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-footer-fg/50 hover:text-primary transition-colors text-sm"
                  >
                    Instagram
                  </a>
                  <a
                    href="https://br.linkedin.com/company/equilybra-clinica-de-psicologia"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-footer-fg/50 hover:text-primary transition-colors text-sm"
                  >
                    LinkedIn
                  </a>
                </div>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-footer-fg/8 relative overflow-hidden">
        <FooterNeuralBar />
        <div className="container mx-auto px-4 py-5 relative z-10">
          <div className="text-center text-sm text-footer-fg/60 space-y-1">
            <p>Razão Social: EQUILYBRA CLINICA DE PSICOLOGIA LTDA.</p>
            <p>CNPJ: 19.534.547/0001-03</p>
            <p>Endereço: RUA AGOSTINHO GOMES 3319 - IPIRANGA - SP</p>
            <p>
              <a href="mailto:contato@equilybra.com.br" className="hover:text-primary transition-colors">
                contato@equilybra.com.br
              </a>
            </p>
            <p>
              <a href="tel:1123050747" className="hover:text-primary transition-colors">
                (11) 2305-0747
              </a>
            </p>
            <p className="pt-2">
              <Link to="/politica-de-privacidade" className="hover:text-primary transition-colors underline-offset-2 hover:underline">
                Política de Privacidade
              </Link>
            </p>
          </div>

          <div className="mt-6 flex justify-center">
            <a
              href="https://transparencyreport.google.com/safe-browsing/search?url=www.equilybra.com.br&hl=pt_BR"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Verificar segurança do site no Google Safe Browsing"
              className="group relative inline-flex items-center gap-2.5 rounded-full border border-emerald-400/30 bg-gradient-to-r from-emerald-500/15 via-emerald-400/10 to-teal-500/15 px-5 py-2.5 text-sm font-semibold text-emerald-100 shadow-[0_4px_20px_-6px_rgba(16,185,129,0.5)] backdrop-blur-md transition-all hover:border-emerald-300/60 hover:shadow-[0_6px_28px_-4px_rgba(16,185,129,0.7)] hover:-translate-y-0.5"
            >
              <span className="relative flex h-6 w-6 items-center justify-center rounded-full bg-emerald-400/20">
                <span className="absolute inset-0 rounded-full bg-emerald-400/40 animate-ping opacity-60" />
                <ShieldCheck className="relative w-3.5 h-3.5 text-emerald-300" />
              </span>
              Site Seguro · Google Safe Browsing
            </a>
          </div>

          <p className="text-center text-sm text-footer-fg/50 mt-2 flex items-center justify-center gap-1">
            Desenvolvido por
            <a
              href="https://www.topodasbuscas.com.br"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 hover:text-primary transition-colors"
            >
              <Search className="w-3.5 h-3.5" />
              Topo das Buscas
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
